import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { CreateVisitasDto } from '../../../types/visitas';
import ClienteSearch from '../../../components/Tesoreria/ClienteSearch';
import DireccionClienteSearch from '../../../components/Tesoreria/DireccionClienteSearch';
import { buildFechaHoraProgramada } from './planFormHelpers';
import PlanVisitaItemRow from './PlanVisitaItemRow';
import {
  emptyVisitRow,
  handleDireccionSelected,
  noSpinnerSx,
  VisitRowState,
} from './planVisitaFormUi';
import { loadDireccionesForVisitRow, validateVisitRow } from './planVisitaRowLoaders';

const formatPlanFechaForHorario = (value?: string | null): string => {
  if (!value) return '';
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
  if (match) return match[1];
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

interface AppendVisitaDialogProps {
  open: boolean;
  planFecha?: string | null;
  saving?: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateVisitasDto) => Promise<void>;
}

const AppendVisitaDialog: React.FC<AppendVisitaDialogProps> = ({
  open,
  planFecha,
  saving = false,
  onClose,
  onSubmit,
}) => {
  const [visitRow, setVisitRow] = useState<VisitRowState>(emptyVisitRow());
  const [clienteSearchOpen, setClienteSearchOpen] = useState(false);
  const [direccionSearchOpen, setDireccionSearchOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const fechaPlan = formatPlanFechaForHorario(planFecha);

  useEffect(() => {
    if (!open) return;
    setVisitRow(emptyVisitRow());
    setClienteSearchOpen(false);
    setDireccionSearchOpen(false);
    setSubmitError(null);
  }, [open]);

  const patchVisitRow = (patch: Partial<VisitRowState>) => {
    setVisitRow((prev) => ({ ...prev, ...patch }));
  };

  const handleClienteSelected = async (cliente: {
    nroCliente: number;
    nombre?: string;
    razonSocial?: string;
  }) => {
    const clienteNombre =
      [cliente.nombre, cliente.razonSocial].filter(Boolean).join(' — ') ||
      `Cliente ${cliente.nroCliente}`;

    setVisitRow({
      ...emptyVisitRow(),
      nroCliente: cliente.nroCliente,
      clienteNombre,
      direccionLoading: true,
    });
    setClienteSearchOpen(false);

    const direccionPatch = await loadDireccionesForVisitRow(cliente.nroCliente);
    setVisitRow((prev) => ({
      ...prev,
      ...direccionPatch,
    }));
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    const validationError = validateVisitRow(visitRow);
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    await onSubmit({
      nroCliente: visitRow.nroCliente,
      codigoDireccion: visitRow.codigoDireccion.trim() || undefined,
      fechaHoraProgramada: buildFechaHoraProgramada(fechaPlan, visitRow.horarioPreferido),
      observaciones: visitRow.observaciones || null,
    });
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" sx={noSpinnerSx}>
        <DialogTitle>Agregar visita al plan</DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Las visitas con horario preferido se ordenan primero, de menor a mayor hora.
          </Typography>
          {submitError ? (
            <Typography color="error" variant="body2" sx={{ mb: 1.5 }}>
              {submitError}
            </Typography>
          ) : null}
          <Box>
            <PlanVisitaItemRow
              row={visitRow}
              onPatch={patchVisitRow}
              onOpenClientePicker={() => setClienteSearchOpen(true)}
              onOpenDireccionPicker={() => setDireccionSearchOpen(true)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={Number(visitRow.nroCliente) <= 0 || saving}
          >
            Agregar
          </Button>
        </DialogActions>
      </Dialog>

      <ClienteSearch
        open={clienteSearchOpen}
        onClose={() => setClienteSearchOpen(false)}
        onSelect={handleClienteSelected}
      />

      <DireccionClienteSearch
        open={direccionSearchOpen}
        onClose={() => setDireccionSearchOpen(false)}
        nroCliente={Number(visitRow.nroCliente) || 0}
        onSelect={(d) => {
          handleDireccionSelected(d, patchVisitRow);
          setDireccionSearchOpen(false);
        }}
      />
    </>
  );
};

export default AppendVisitaDialog;
